import React, { useState, useEffect } from 'react';
import { 
  createMemory, 
  updateMemory, 
  deleteMemory, 
  deleteMemories,
  getCategoryInfo, 
  formatMemoryDate, 
  extractEntitiesFromMemory,
  getMemorySentiment,
  MEMORY_CATEGORIES 
} from '../../../../../services/memoryService';

const MemoryEditModal = ({ type, memory, selectedMemories, userId, onClose, onSuccess, onEdit }) => {
  // Form state
  const [formData, setFormData] = useState({
    category: '',
    utterance: '',
    summary: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (type === 'edit' && memory) {
      setFormData({
        category: memory.category || '',
        utterance: memory.utterance || '',
        summary: memory.summary || ''
      });
    } else if (type === 'create') {
      setFormData({
        category: '',
        utterance: '',
        summary: ''
      });
    }
    setErrors({});
  }, [type, memory]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.utterance.trim()) {
      newErrors.utterance = 'Memory content is required';
    } else if (formData.utterance.trim().length < 10) {
      newErrors.utterance = 'Memory content must be at least 10 characters';
    } else if (formData.utterance.length > 2000) {
      newErrors.utterance = 'Memory content must be less than 2000 characters';
    }

    if (formData.summary && formData.summary.length > 500) {
      newErrors.summary = 'Summary must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      let reprocess;
      if (type === 'create') {
        const result = await createMemory({
          ...formData,
          user_id: userId,
          utterance: formData.utterance.trim(),
          summary: formData.summary.trim() || null
        });

        if (result.error) {
          throw new Error(result.error);
        }
        reprocess = result.reprocess;
      } else if (type === 'edit' && memory) {
        const result = await updateMemory(memory.memory_id, {
          ...formData,
          utterance: formData.utterance.trim(),
          summary: formData.summary.trim() || null
        });

        if (result.error) {
          throw new Error(result.error);
        }
        reprocess = result.reprocess;
      }

      onSuccess(reprocess);
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to save memory' });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this memory? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const result = await deleteMemory(memory.memory_id);
      
      if (result.error) {
        throw new Error(result.error);
      }

      onSuccess();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to delete memory' });
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    const count = selectedMemories?.size || 0;
    if (count === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${count} memories? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const memoryIds = Array.from(selectedMemories);
      const result = await deleteMemories(memoryIds);
      
      if (result.error) {
        throw new Error(result.error);
      }

      onSuccess();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to delete memories' });
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Render different modal types
  const renderModalContent = () => {
    switch (type) {
      case 'create':
      case 'edit':
        return renderForm();
      case 'view':
        return renderView();
      case 'delete':
        return renderDeleteConfirm();
      case 'bulkDelete':
        return renderBulkDeleteConfirm();
      default:
        return null;
    }
  };

  // Render form for create/edit
  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Category Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(MEMORY_CATEGORIES).map(([key, category]) => (
            <button
              key={key}
              type="button"
              onClick={() => handleInputChange('category', key)}
              className={`p-3 rounded-lg border text-left transition-all ${
                formData.category === key
                  ? `${category.color} border-current`
                  : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">{category.icon}</span>
                <span className="text-sm font-medium">{category.label}</span>
              </div>
            </button>
          ))}
        </div>
        {errors.category && (
          <p className="mt-1 text-sm text-red-600">{errors.category}</p>
        )}
      </div>

      {/* Memory Content */}
      <div>
        <label htmlFor="utterance" className="block text-sm font-medium text-gray-700 mb-1">
          Memory Content <span className="text-red-500">*</span>
        </label>
        <textarea
          id="utterance"
          value={formData.utterance}
          onChange={(e) => handleInputChange('utterance', e.target.value)}
          rows={6}
          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
            errors.utterance ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Describe the memory in detail..."
        />
        <div className="mt-1 flex justify-between text-xs text-gray-500">
          <span>{errors.utterance || 'Minimum 10 characters'}</span>
          <span>{formData.utterance.length}/2000</span>
        </div>
      </div>

      {/* Submit Error */}
      {errors.submit && (
        <div className="p-3 bg-red-100 border border-red-200 text-red-700 rounded-md text-sm">
          {errors.submit}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-between">
        <div>
          {type === 'edit' && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={loading}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              🗑️ Delete
            </button>
          )}
        </div>
        
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? 'Saving...' : type === 'create' ? 'Create Memory' : 'Save Changes'}
          </button>
        </div>
      </div>
    </form>
  );

  // Render view-only mode
  const renderView = () => {
    if (!memory) return null;

    const categoryInfo = getCategoryInfo(memory.category);
    const entities = extractEntitiesFromMemory(memory);
    const sentiment = getMemorySentiment(memory);

    return (
      <div className="space-y-6">
        {/* Category and Date */}
        <div className="flex items-center justify-between">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${categoryInfo.color}`}>
            {categoryInfo.icon} {categoryInfo.label}
          </span>
          <span className="text-sm text-gray-500">
            Created {formatMemoryDate(memory.created_at)}
          </span>
        </div>

        {/* Memory Content */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Memory Content</h4>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
              {memory.utterance}
            </p>
          </div>
        </div>

        {/* Summary */}
        {memory.summary && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Summary</h4>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-blue-700">{memory.summary}</p>
            </div>
          </div>
        )}

        {/* Entities */}
        {entities.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Extracted Entities</h4>
            <div className="flex flex-wrap gap-2">
              {entities.map((entity, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                >
                  <span className="font-medium">{entity.type}:</span>
                  <span className="ml-1">{entity.value}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Sentiment */}
        {sentiment && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Sentiment Analysis</h4>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-2 py-1 rounded text-sm ${sentiment.color}`}>
                {sentiment.label}
              </span>
              {sentiment.score && (
                <span className="text-xs text-gray-500">
                  ({Math.round(sentiment.score * 100)}% confidence)
                </span>
              )}
            </div>
          </div>
        )}

        {/* Metadata */}
        {memory.source && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Source Information</h4>
            <div className="bg-gray-50 rounded-lg p-3">
              <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                {JSON.stringify(memory.source, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t">
          <button
            onClick={handleDelete}
            disabled={loading}
            className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            🗑️ Delete Memory
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Close
            </button>
            <button
              onClick={() => {
                if (onEdit) {
                  onEdit(memory);
                } else {
                  onClose();
                }
              }}
              className="px-4 py-2 border border-transparent text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              ✏️ Edit Memory
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render delete confirmation
  const renderDeleteConfirm = () => (
    <div className="text-center space-y-4">
      <div className="text-red-600 text-4xl">⚠️</div>
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Memory</h3>
        <p className="text-gray-600">
          Are you sure you want to delete this memory? This action cannot be undone.
        </p>
      </div>
      
      {memory && (
        <div className="bg-gray-50 rounded-lg p-4 text-left">
          <div className="text-sm text-gray-700">
            <strong>Category:</strong> {getCategoryInfo(memory.category).label}
          </div>
          <div className="text-sm text-gray-700 mt-1">
            <strong>Content:</strong> {memory.utterance.substring(0, 100)}
            {memory.utterance.length > 100 && '...'}
          </div>
        </div>
      )}

      {errors.submit && (
        <div className="p-3 bg-red-100 border border-red-200 text-red-700 rounded-md text-sm">
          {errors.submit}
        </div>
      )}

      <div className="flex justify-center space-x-3">
        <button
          onClick={onClose}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          onClick={handleDelete}
          disabled={loading}
          className="px-4 py-2 border border-transparent text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          {loading ? 'Deleting...' : 'Delete Memory'}
        </button>
      </div>
    </div>
  );

  // Render bulk delete confirmation
  const renderBulkDeleteConfirm = () => {
    const count = selectedMemories?.size || 0;
    
    return (
      <div className="text-center space-y-4">
        <div className="text-red-600 text-4xl">⚠️</div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Multiple Memories</h3>
          <p className="text-gray-600">
            Are you sure you want to delete <strong>{count} memories</strong>? This action cannot be undone.
          </p>
        </div>

        {errors.submit && (
          <div className="p-3 bg-red-100 border border-red-200 text-red-700 rounded-md text-sm">
            {errors.submit}
          </div>
        )}

        <div className="flex justify-center space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={loading}
            className="px-4 py-2 border border-transparent text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            {loading ? 'Deleting...' : `Delete ${count} Memories`}
          </button>
        </div>
      </div>
    );
  };

  // Get modal title
  const getModalTitle = () => {
    switch (type) {
      case 'create':
        return 'Create New Memory';
      case 'edit':
        return 'Edit Memory';
      case 'view':
        return 'Memory Details';
      case 'delete':
        return 'Delete Memory';
      case 'bulkDelete':
        return 'Delete Multiple Memories';
      default:
        return 'Memory';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 xl:w-2/5 shadow-lg rounded-md bg-white max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start mb-4 pb-4 border-b">
          <h3 className="text-xl font-semibold text-gray-900">{getModalTitle()}</h3>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {renderModalContent()}
        </div>
      </div>
    </div>
  );
};

export default MemoryEditModal;